/* eslint-disable @typescript-eslint/no-use-before-define */

import { useCallback } from 'react';

import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import { ApplicantType, ApplicationType } from 'features/Onboarding/types';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import Compliance from '../Compliance/Compliance';
import PersonalAddress from '../PersonalAddress/PersonalAddress';
import machineConfig, { model, State } from './machine';

type ComplianceAndPersonalAddressProps = {
  companyProfileId: string;
  applicantType: ApplicantType;
  applicationType: ApplicationType;
};

const ComplianceAndPersonalAddress =
  asBentoModule<ComplianceAndPersonalAddressProps>(
    ({ applicantType, applicationType, companyProfileId, onDone }) => (
      <ComplianceAndPersonalAddressComponent
        moduleData={{
          applicantType,
          applicationType,
          companyProfileId,
        }}
        onDone={onDone}
      />
    ),
  );

const ComplianceAndPersonalAddressComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const onPrev = useCallback(() => {
      send(model.events.PREV());
    }, [send]);

    const stringifiedState = stringifyState<State>(state);

    switch (stringifiedState) {
      case State.PERSONAL_ADDRESS:
        return (
          <PersonalAddress
            applicantType={moduleData.applicantType}
            companyProfileId={moduleData.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : send(model.events.NEXT())
            }
          />
        );

      case State.COMPLIANCE:
        return (
          <Compliance
            applicationType={moduleData.applicationType}
            companyProfileId={moduleData.companyProfileId}
            onDone={({ status }) =>
              status === BentoModuleDoneStatus.ABORT
                ? onPrev()
                : send(model.events.NEXT())
            }
          />
        );

      case State.DONE:
      case State.ABORT:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },
  {
    devTools: true,
    machineConfig,
  },
);

export default ComplianceAndPersonalAddress;
